/**
 * Multi-key invalidation shortcuts. For single-key invalidations, use
 * `queryClient.invalidateQueries({ queryKey: queryKeys.… })` directly — this
 * file exists to name the common groupings (e.g. "account state" really means
 * invalidating both perp account and spot balances).
 */
import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "./query-keys";

/** Invalidate account + spot-balances (used after transfers, leverage updates, etc.) */
export function invalidateAccountState(
	queryClient: QueryClient,
	address: string | null,
) {
	return Promise.all([
		queryClient.invalidateQueries({
			queryKey: queryKeys.hyper.account(address),
		}),
		queryClient.invalidateQueries({
			queryKey: queryKeys.hyper.spotBalances(address),
		}),
	]);
}

/**
 * Default delay for the trade-driven account refetch backstop. Long enough for
 * Hyperliquid's REST `clearinghouseState` to become consistent after a fill.
 */
export const ACCOUNT_REFETCH_BACKSTOP_MS = 2_000;

const pendingAccountRefetch = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Debounce plumbing shared by the `schedule*Refetch` helpers: one timer per
 * key; scheduling again before it fires resets it, so rapid calls coalesce
 * into a single `run`.
 */
function scheduleCoalesced(
	pending: Map<string, ReturnType<typeof setTimeout>>,
	key: string,
	delayMs: number,
	run: () => void,
) {
	const existing = pending.get(key);
	if (existing) clearTimeout(existing);
	const timer = setTimeout(() => {
		pending.delete(key);
		run();
	}, delayMs);
	pending.set(key, timer);
}

/**
 * Debounced, **delayed** account-state refetch — use this instead of
 * `invalidateAccountState` for any *fill-/trade-driven* refresh.
 *
 * Why this exists: Hyperliquid's REST `clearinghouseState` has read-after-write
 * lag — for a brief window right after a fill it still returns the *pre-fill*
 * position. The real-time WS sync (`useHyperMainSync` for main, `useHyperXyzSync`
 * for xyz) already pushes the fresh position into the `account` cache the instant
 * a fill lands. Invalidating `account` *immediately* on that same fill forces a
 * REST refetch that resolves a few hundred ms later with the stale snapshot and
 * **overwrites** the fresh WS value — the position "lags by one fill"
 * (permanently for xyz, since the main sync can only re-merge the now-stale
 * cached xyz). See `features/trade/ARCHITECTURE.md` → "Real-time sync".
 *
 * Scheduling one coalesced refetch per address fixes this: by the time it fires,
 * HL REST is consistent, so it can only confirm the WS value, never clobber it
 * with stale data. Rapid consecutive fills reset the timer → a single refetch.
 *
 * Keep `invalidateAccountState` for non-fill cases that genuinely need an
 * immediate read (e.g. the first-subscribe backstop) where there is no fresher
 * WS value to protect.
 */
export function scheduleAccountStateRefetch(
	queryClient: QueryClient,
	address: string | null,
	delayMs = ACCOUNT_REFETCH_BACKSTOP_MS,
) {
	if (!address) return;
	scheduleCoalesced(pendingAccountRefetch, address, delayMs, () => {
		void invalidateAccountState(queryClient, address);
	});
}

/**
 * Default delay for the fill-history refetch triggered by a WS reconnect
 * snapshot. Same rationale as `ACCOUNT_REFETCH_BACKSTOP_MS`: right after a
 * fill, HL REST can still return pre-fill data, which would overwrite fresher
 * WS-prepended fills — by the time this fires, REST is consistent.
 */
export const FILL_HISTORY_REFETCH_BACKSTOP_MS = 2_000;

const pendingFillHistoryRefetch = new Map<
	string,
	ReturnType<typeof setTimeout>
>();

/**
 * Delayed, coalesced refetch of the fill-derived histories (`fills` +
 * `orderHistory`) — the recovery path for fills that landed while the user WS
 * was disconnected (`useHyperUserEvents`' reconnect-snapshot branch).
 *
 * Both caches are otherwise kept fresh only by per-event WS pushes, so a
 * missed event has no other way back in. Coalescing per address keeps a flaky
 * connection (WS reconnects retry forever) from becoming a REST refetch storm
 * — refetching the fills infinite query re-requests every loaded page.
 */
export function scheduleFillHistoryRefetch(
	queryClient: QueryClient,
	address: string | null,
	delayMs = FILL_HISTORY_REFETCH_BACKSTOP_MS,
) {
	if (!address) return;
	scheduleCoalesced(pendingFillHistoryRefetch, address, delayMs, () => {
		void queryClient.invalidateQueries({
			queryKey: queryKeys.hyper.fills(address),
		});
		void queryClient.invalidateQueries({
			queryKey: queryKeys.hyper.orderHistory(address),
		});
	});
}

/** Invalidate wallet balances on Arbitrum */
export function invalidateWalletBalances(
	queryClient: QueryClient,
	address: string | null,
) {
	return queryClient.invalidateQueries({
		queryKey: queryKeys.wallet.balances(address),
	});
}

/** Invalidate open orders */
export function invalidateOpenOrders(
	queryClient: QueryClient,
	address: string | null,
) {
	return queryClient.invalidateQueries({
		queryKey: queryKeys.hyper.openOrders(address),
	});
}
